import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import api, { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const [detail, setDetail] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setDetail(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            cleared.current = true;
            clear();
            if (user) refresh();
          }
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("error");
          return;
        }
      } catch {
        setStatus("error");
        return;
      }
      if (attempts++ < 8) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="max-w-xl mx-auto px-6 py-32 text-center min-h-[70vh] flex flex-col items-center justify-center">
      {status === "checking" ? (
        <>
          <Loader2 size={40} className="animate-spin text-champagne" />
          <p className="text-stone-500 mt-6">{t("payment.processing")}</p>
        </>
      ) : status === "paid" ? (
        <>
          <div className="w-16 h-16 rounded-full bg-charcoal flex items-center justify-center">
            <Check size={30} className="text-white" strokeWidth={2} />
          </div>
          <h1 className="font-serif text-4xl tracking-tight text-charcoal mt-8" data-testid="payment-success-title">
            {t("payment.success.title")}
          </h1>
          <p className="text-stone-500 mt-4">{t("payment.success.body")}</p>
          {detail?.items?.length > 0 && (
            <div className="mt-8 w-full border-t border-stone-200 pt-6 text-left">
              {detail.items.map((it, i) => (
                <div key={i} className="flex justify-between text-sm text-stone-600 py-1">
                  <span>{it.quantity} × {it.name}</span>
                  <span>{formatPrice(it.price * it.quantity)}</span>
                </div>
              ))}
              <div className="flex justify-between text-charcoal font-medium border-t border-stone-200 mt-3 pt-3">
                <span>{t("cart.subtotal")}</span>
                <span>{formatPrice(detail.amount)}</span>
              </div>
            </div>
          )}
          <Link
            to="/"
            data-testid="success-home-cta"
            className="mt-10 inline-block bg-charcoal text-white px-10 py-4 text-xs uppercase tracking-[0.2em] hover:bg-stone-800 transition-colors"
          >
            {t("payment.success.cta")}
          </Link>
        </>
      ) : (
        <>
          <h1 className="font-serif text-3xl text-charcoal">{t("payment.processing")}</h1>
          <Link to="/" className="mt-8 inline-block border-b border-charcoal pb-1 text-xs uppercase tracking-[0.2em]">
            {t("payment.success.cta")}
          </Link>
        </>
      )}
    </div>
  );
};

export default PaymentSuccess;
